import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/contexts/analytics/hooks";
import type { AnnieChatItem } from "@/hooks/useAnnie";
import { CHAT_ACTION_BAR_REVEAL_SX, ChatActionBar } from "@/ui/Chat/ChatActionBar";
import { ChatCopyButton } from "@/ui/Chat/ChatCopyButton";
import { ChatGuidedOffer } from "@/ui/Chat/ChatGuidedOffer";
import { ChatMarkdown } from "@/ui/Chat/ChatMarkdown";
import { ChatMessageBubble } from "@/ui/Chat/ChatMessageBubble";
import { ChatRegenerateButton } from "@/ui/Chat/ChatRegenerateButton";
import { ChatSources } from "@/ui/Chat/ChatSources";
import { ChatSuggestions } from "@/ui/Chat/ChatSuggestions";
import { ChatWorkSteps } from "@/ui/Chat/ChatWorkSteps";
import { ChatWorkStepsMode } from "@/ui/Chat/chatWorkStepTypes";
import { formatMediaDuration } from "@/ui/Media/formatMediaDuration";
import type { AnnieFeedbackRequest, AnnieNurseChoiceOption, AnnieSuggestion, AnnieVideo, AnnieWalkthrough } from "@/utils/annieStream";
import { AnnieAnswerFeedback } from "./AnnieAnswerFeedback";
import { chatWorkSteps, MIN_LISTED_WORK_STEPS } from "./annieWorkSteps";

type AnnieAnswerItem = Extract<AnnieChatItem, { kind: "assistant" }>;

type Props = {
	item: AnnieAnswerItem;
	/** The user's name for Annie. */
	assistantName: string;
	/** Only the latest answer is regenerable: the backend rejects older targets to keep the transcript consistent. */
	isLatest: boolean;
	onRegenerate: (answerItemId: string, conversationId: number) => void;
	onSuggestionSelect: (suggestion: AnnieSuggestion) => void;
	onNurseChoiceSelect: (itemId: string, name: string, option: AnnieNurseChoiceOption) => void;
	nurseChoicesDisabled: boolean;
	onStartWalkthrough?: (walkthrough: AnnieWalkthrough) => void;
	onWatchVideo: (video: AnnieVideo) => void;
	onSaveFeedback: (itemId: string, conversationId: number, replacement: AnnieFeedbackRequest) => Promise<boolean>;
};

/** A read answer: markdown, sources, follow-ups, the guided-tour offer and the answer's action bar. */
export const AnnieAnswerBubble: FC<Props> = ({
	item,
	assistantName,
	isLatest,
	onRegenerate,
	onSuggestionSelect,
	onNurseChoiceSelect,
	nurseChoicesDisabled,
	onStartWalkthrough,
	onWatchVideo,
	onSaveFeedback,
}) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { conversationId, feedback, walkthrough, workSteps } = item;

	const answerActions = (
		<>
			<ChatCopyButton
				text={item.text}
				label={t("annie.answer.copy")}
				copiedLabel={t("annie.answer.copied")}
				dataTestId={`annie-answer-copy-${item.id}`}
				onError={(error) => trackError("annie_answer_copy_failed", { error_message: error instanceof Error ? error.message : String(error) })}
			/>
			{conversationId !== undefined && isLatest && (
				<ChatRegenerateButton
					label={t("annie.answer.regenerate")}
					onClick={() => onRegenerate(item.id, conversationId)}
					dataTestId={`annie-answer-regenerate-${item.id}`}
				/>
			)}
		</>
	);

	return (
		<ChatMessageBubble sender="assistant" maxWidth="100%" dataTestId={`annie-message-${item.id}`} sx={CHAT_ACTION_BAR_REVEAL_SX}>
			{workSteps !== undefined && workSteps.length >= MIN_LISTED_WORK_STEPS && (
				<ChatWorkSteps
					mode={ChatWorkStepsMode.Summary}
					steps={chatWorkSteps(workSteps, null)}
					summaryLabel={t("annie.work_steps.summary", { count: workSteps.length })}
					onToggle={(isOpen) => track("annie_work_steps_toggled", { is_open: isOpen, step_count: workSteps.length })}
					ariaLabel={t("annie.work_steps.aria_label", { name: assistantName })}
					dataTestId={`annie-answer-work-steps-${item.id}`}
					sx={{ mb: 0.5 }}
				/>
			)}
			<ChatMarkdown dataTestId={`annie-answer-${item.id}`}>{item.text}</ChatMarkdown>
			{item.sources !== undefined && item.sources.length > 0 && (
				<ChatSources label={t("annie.answer.sources")} sources={item.sources} dataTestId={`annie-answer-sources-${item.id}`} />
			)}
			{item.nurseChoices?.map((group, groupIndex) => (
				<ChatSuggestions
					key={group.name}
					label={t("annie.answer.which_nurse", { name: group.name })}
					suggestions={group.options.map((option) => ({ ...option, id: String(option.nurse_id), title: option.label }))}
					onSelect={(option) => onNurseChoiceSelect(item.id, group.name, option)}
					ariaLabel={(label) => t("annie.answer.select_nurse", { name: label })}
					disabled={nurseChoicesDisabled}
					dataTestId={`annie-answer-nurse-choices-${item.id}-${groupIndex}`}
				/>
			))}
			{item.suggestions !== undefined && item.suggestions.length > 0 && (
				<ChatSuggestions
					label={t("annie.answer.related")}
					suggestions={item.suggestions}
					onSelect={onSuggestionSelect}
					ariaLabel={(title) => t("annie.answer.followup", { topic: title })}
					dataTestId={`annie-answer-suggestions-${item.id}`}
				/>
			)}
			{walkthrough !== undefined && onStartWalkthrough !== undefined && (
				<ChatGuidedOffer
					title={t(walkthrough.titleKey)}
					meta={t("annie.walkthrough.offer.meta")}
					ariaLabel={t(walkthrough.destinationKey)}
					onStart={() => onStartWalkthrough(walkthrough)}
					onShown={() => track("annie_walkthrough_offer_shown", { topic_id: walkthrough.topicId })}
					dataTestId={`annie-answer-walkthrough-${item.id}`}
				/>
			)}
			{item.videos !== undefined && item.videos.length > 0 && (
				<ChatSuggestions
					label={t("annie.answer.videos")}
					suggestions={item.videos.map((video) => ({
						...video,
						title: t("annie.answer.video_chip", { title: video.title, duration: formatMediaDuration(video.durationSeconds) }),
					}))}
					onSelect={onWatchVideo}
					ariaLabel={(title) => t("annie.answer.watch_video", { title })}
					dataTestId={`annie-answer-videos-${item.id}`}
				/>
			)}
			{feedback !== undefined && conversationId !== undefined ? (
				<AnnieAnswerFeedback
					conversationId={conversationId}
					feedback={feedback}
					isSaving={item.isSavingFeedback === true}
					onSave={(replacement) => onSaveFeedback(item.id, conversationId, replacement)}
					actions={answerActions}
				/>
			) : (
				<ChatActionBar dataTestId={`annie-answer-actions-${item.id}`}>{answerActions}</ChatActionBar>
			)}
		</ChatMessageBubble>
	);
};
